import { ChangeEvent, KeyboardEvent } from "react";
import { v4 as uuid } from "uuid";
import { X } from "lucide-react";

import {
	PropertyHotelisePropertyDetailsCategory,
	PropertyHotelisePropertyDetailsCategoryField,
} from "models/properties";

interface Props {
	details: PropertyHotelisePropertyDetailsCategory;
	onUpdate: (category: PropertyHotelisePropertyDetailsCategory) => void;
	isEditActive: boolean;
	onEndEditing: (categoryId: string) => void;
	onDelete: (id: string) => void;
	isLastItem: boolean;
}

const PropertyDetailsCard = (props: Props): JSX.Element => {
	const {
		details,
		onUpdate,
		isEditActive,
		onEndEditing,
		onDelete,
		isLastItem,
	} = props;

	const { fields, name } = details;

	const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;

		onUpdate({
			...details,
			name: value,
		});
	};

	const onChangeFieldName = (id: string, newFieldName: string) => {
		const updatedFields = fields.map((fieldItem) => {
			if (fieldItem.id === id) {
				return {
					...fieldItem,
					name: newFieldName,
				};
			}

			return fieldItem;
		});

		onUpdate({
			...details,
			fields: updatedFields,
		});
	};

	const onChangeField = (id: string, value: string) => {
		const updatedFields = fields.map((fieldItem) => {
			if (fieldItem.id === id) {
				return {
					...fieldItem,
					value,
				};
			}

			return fieldItem;
		});

		onUpdate({
			...details,
			fields: updatedFields,
		});
	};

	const handleCreateNewField = () => {
		const updatedFields = [
			...fields,
			{
				id: uuid(),
				name: "",
				value: "",
			},
		];

		onUpdate({
			...details,
			fields: updatedFields,
		});
	};

	const handleDeleteField = (id: string) => {
		const filteredFields = fields.filter((fieldItem) => fieldItem.id !== id);

		onUpdate({
			...details,
			fields: filteredFields,
		});
	};

	const handleKeyDown = (
		event: KeyboardEvent<HTMLInputElement>,
		fieldId?: string,
		index?: number
	) => {
		if (event.key === "Enter") {
			onEndEditing(details.id);

			if (index === fields.length - 1) {
				const isFieldFilled =
					fields[index].name.length || fields[index].value.length;

				if (isFieldFilled) {
					handleCreateNewField();
				}
			}
		}
	};

	const handleOnBlur = () => {
		const lastField = fields[fields.length - 1];
		onEndEditing(details.id);

		if ((lastField && lastField.name.length) || lastField.value.length) {
			handleCreateNewField();
		}
	};

	const renderItem = (
		field: PropertyHotelisePropertyDetailsCategoryField,
		index: number
	) => (
		<div key={field.id} className="w-full mb-[15px] grid grid-cols-[1fr_40px] gap-2.5 px-2.5 py-[5px] relative hover:bg-secondary/10 last:mb-0 [&_input]:mb-[5px] [&_input:last-child]:mb-0 [&_p]:mb-[5px] [&_p:last-child]:mb-0">
			<div>
				{isEditActive ? (
					<input
						onKeyDown={(event) => handleKeyDown(event, field.id, index)}
						value={field.name}
						onChange={(event) =>
							onChangeFieldName(field.id, event.target.value)
						}
						placeholder="label"
						onBlur={handleOnBlur}
						className="w-full border-none bg-transparent rounded outline-none h-5 text-sm text-foreground placeholder:text-muted-foreground"
					/>
				) : (
					<p className="text-muted-foreground w-full font-medium text-sm">{field.name || "label"}</p>
				)}
				{isEditActive ? (
					<input
						onKeyDown={(event) => handleKeyDown(event, field.id, index)}
						value={field.value}
						onChange={(event) => onChangeField(field.id, event.target.value)}
						placeholder="new Field"
						onBlur={handleOnBlur}
						className="w-full border-none bg-transparent rounded outline-none h-5 text-base text-foreground placeholder:text-muted-foreground"
					/>
				) : (
					<p className="w-full h-5 text-base text-foreground">{field.value || "new Field"}</p>
				)}
			</div>
			{index !== fields.length - 1 ? (
				<div className="flex justify-center items-center">
					{isEditActive ? (
						<button
							onClick={() => handleDeleteField(field.id)}
							className="border border-destructive rounded-full h-7 w-7 flex justify-center items-center bg-transparent cursor-pointer"
						>
							<X className="h-4 w-4 text-destructive" />
						</button>
					) : null}
				</div>
			) : null}
		</div>
	);

	const renderFields = () =>
		fields.map(
			(field: PropertyHotelisePropertyDetailsCategoryField, index: number) =>
				renderItem(field, index)
		);

	const renderTitle = () => (
		<div className="mb-[15px] px-2.5 py-[5px] relative w-full grid grid-cols-[1fr_40px] gap-2.5 hover:bg-secondary/10">
			{isEditActive ? (
				<>
					<input
						onKeyDown={(event) => handleKeyDown(event)}
						value={name}
						onChange={onChangeName}
						placeholder="SECTION"
						onBlur={() => onEndEditing(details.id)}
						className="w-full border-none bg-transparent rounded outline-none h-5 text-lg text-foreground uppercase placeholder:text-muted-foreground"
					/>
					{!isLastItem && isEditActive ? (
						<div className="flex justify-center items-center">
							<button
								onClick={() => onDelete(details.id)}
								className="border border-destructive rounded-full h-7 w-7 flex justify-center items-center bg-transparent cursor-pointer"
							>
								<X className="h-4 w-4 text-destructive" />
							</button>
						</div>
					) : null}
				</>
			) : (
				<p className="w-full font-bold text-lg text-foreground h-5 uppercase">{name || "SECTION"}</p>
			)}
		</div>
	);

	return (
		<div className="p-[15px] relative flex flex-col">
			{renderTitle()}
			{renderFields()}
		</div>
	);
};

export default PropertyDetailsCard;
